import { SyntheticEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Snackbar,
  Tab,
  Tabs,
  TextField,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import EditIcon from '@mui/icons-material/Edit';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import { AddFoodstuff } from './AddFoodstuff';
import { AddMeal } from './AddMeal';
import { RootState } from '../../app/store';
import { Meal } from '../../models/Meal';
import {
  deleteUserMeal,
  getAllMealsByAuthor,
  getLikeMeals,
  saveConsumedMeal,
  saveUserMealForUser
} from '../../state/meals/mealSlice';
import { calculateNutritionFromMeal } from '../../common/mealMetrics';
import { buildConsumedMealFoodstuffs } from '../../common/consumedMealPayload';

type TabIndex = 0 | 1 | 2;

function resolveMealtimeLabel(meal: Meal): string {
  if (!meal.mealtime) {
    return '—';
  }

  const labels: Record<string, string> = {
    BREAKFAST: 'Snídaně',
    SNACK_AM: 'Svačina (ráno)',
    LUNCH: 'Oběd',
    SNACK_PM: 'Svačina (odpoledne)',
    DINNER: 'Večeře'
  };

  return labels[meal.mealtime] || meal.mealtime;
}

function formatCalories(kcal: number | undefined): string {
  if (!kcal || !Number.isFinite(kcal)) {
    return '--';
  }

  return `${kcal.toFixed(0)} kcal`;
}

function mainIngredients(meal: Meal): string {
  const names = (meal.foodstuffs || [])
    .map((foodstuff) => foodstuff.name)
    .filter((name): name is string => Boolean(name?.trim()));

  if (names.length === 0) {
    return 'Bez ingrediencí';
  }

  return names.slice(0, 3).join(', ');
}

function matchesQuery(meal: Meal, query: string): boolean {
  if (!query.trim()) {
    return true;
  }

  const haystack = [`${meal.name || ''}`.toLowerCase(), `${mainIngredients(meal)}`.toLowerCase()].join(' ');
  return haystack.includes(query.toLowerCase());
}

function uniqueById(meals: Meal[]): Meal[] {
  const seen = new Set<string>();

  return meals.filter((meal) => {
    if (!meal.id) {
      return true;
    }

    if (seen.has(meal.id)) {
      return false;
    }

    seen.add(meal.id);
    return true;
  });
}

function guessMealtime(): string {
  const hour = new Date().getHours();
  if (hour < 10) return 'BREAKFAST';
  if (hour < 12) return 'SNACK_AM';
  if (hour < 15) return 'LUNCH';
  if (hour < 18) return 'SNACK_PM';
  return 'DINNER';
}

function MealLibraryCard(props: {
  meal: Meal;
  isFavorite: boolean;
  onLog: () => void;
  onEdit: () => void;
  onToggleFavorite: () => void;
}) {
  const nutrition = calculateNutritionFromMeal(props.meal);
  const isActionDisabled = !props.meal.id;

  return (
    <Card
      sx={{
        borderRadius: 3,
        backgroundColor: '#FFFFFF',
        border: '1px solid #E6E1DB',
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5
      }}
    >
      <CardContent>
        <Typography variant="h6" sx={{ color: '#2E2A26', fontWeight: 700 }}>
          {props.meal.name || 'Neznámý název jídla'}
        </Typography>
        <Typography variant="body2" sx={{ color: '#6F675F', mt: 0.5 }}>
          {resolveMealtimeLabel(props.meal)}
        </Typography>
        <Typography variant="body2" sx={{ color: '#2E2A26', fontWeight: 600, mt: 1 }}>
          {formatCalories(nutrition.energyKcal)}
        </Typography>
        <Typography variant="body2" sx={{ color: '#6F675F', mt: 0.75 }}>
          Hlavní ingredience: {mainIngredients(props.meal)}
        </Typography>
      </CardContent>
      <Divider />
      <CardActions
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 1,
          p: 1.5
        }}
      >
        <Button
          fullWidth
          variant="contained"
          size="small"
          onClick={props.onLog}
          startIcon={<RestaurantIcon />}
          disabled={isActionDisabled}
          sx={{
            backgroundColor: '#7A4A2E',
            '&:hover': { backgroundColor: '#5F3923' },
            textTransform: 'none'
          }}
        >
          Zaznamenat
        </Button>
        <Button
          fullWidth
          variant="outlined"
          size="small"
          onClick={props.onEdit}
          startIcon={<EditIcon />}
          disabled={isActionDisabled}
          sx={{ color: '#6F675F', borderColor: '#D7CFC6', textTransform: 'none' }}
        >
          Upravit
        </Button>
        <Button
          fullWidth
          variant="outlined"
          size="small"
          onClick={props.onToggleFavorite}
          startIcon={props.isFavorite ? <FavoriteIcon /> : <FavoriteBorderIcon />}
          disabled={isActionDisabled}
          sx={{
            color: '#6F675F',
            borderColor: '#D7CFC6',
            textTransform: 'none',
            whiteSpace: 'nowrap'
          }}
        >
          {props.isFavorite ? 'Odebrat z oblíbených' : 'Přidat do oblíbených'}
        </Button>
      </CardActions>
    </Card>
  );
}

export function Meals() {
  const params = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();
  const routeUserId = params.userId;
  const loggedUserId = useSelector((state: RootState) => state.user.user.id);
  const userId = loggedUserId || routeUserId;

  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [activeTab, setActiveTab] = useState<TabIndex>(0);
  const [myMeals, setMyMeals] = useState<Meal[]>([]);
  const [favoriteMeals, setFavoriteMeals] = useState<Meal[]>([]);
  const [catalogMeals, setCatalogMeals] = useState<Meal[]>([]);
  const [tabLoading, setTabLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showFoodstuffDialog, setShowFoodstuffDialog] = useState(false);
  const [showMealDialog, setShowMealDialog] = useState(false);

  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  const filteredMyMeals = useMemo(
    () => myMeals.filter((meal) => matchesQuery(meal, debouncedSearch)),
    [debouncedSearch, myMeals]
  );

  const showEmptyState = useMemo(() => {
    if (activeTab === 0) {
      return filteredMyMeals.length === 0;
    }

    if (activeTab === 1) {
      return favoriteMeals.length === 0;
    }

    return catalogMeals.length === 0;
  }, [activeTab, catalogMeals.length, favoriteMeals.length, filteredMyMeals.length]);

  const emptyText = useMemo(() => {
    if (activeTab === 0) {
      return 'Zatím nemáte vlastní jídla.';
    }
    if (activeTab === 1) {
      return 'Zatím nemáte oblíbená jídla.';
    }

    return 'Nenašli jsme žádná jídla.';
  }, [activeTab]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 250);

    return () => clearTimeout(timeout);
  }, [searchQuery]);

  const fetchMyMeals = useCallback(async () => {
    setTabLoading(true);
    try {
      const result = (await dispatch(getAllMealsByAuthor()).unwrap()) as Meal[];
      setMyMeals(uniqueById(result));
      setErrorMessage('');
    } catch (_error) {
      setErrorMessage('Nepodařilo se načíst vaše jídla.');
    } finally {
      setTabLoading(false);
    }
  }, [dispatch]);

  const fetchFavoritesOrCatalog = useCallback(async () => {
    // TODO: dedicated favorites endpoint has not been exposed in the generated API client.
    // Current fallback: use search endpoint and split by `userMealId`.
    const like = debouncedSearch.trim() || '%';
    setTabLoading(true);

    try {
      const result = (await dispatch(getLikeMeals(like)).unwrap()) as Meal[];
      const userRelated: Meal[] = [];
      const catalog: Meal[] = [];
      const resolvedUserId = routeUserId || loggedUserId;

      result.forEach((meal) => {
        const isOwnedByCurrentUser = resolvedUserId && meal.author?.id === resolvedUserId;
        const isFavorite = Boolean(meal.userMealId);

        if (isFavorite) {
          userRelated.push(meal);
        }

        if (!isFavorite && !isOwnedByCurrentUser) {
          catalog.push(meal);
        }
      });

      setFavoriteMeals(uniqueById(userRelated));
      setCatalogMeals(uniqueById(catalog));
      setErrorMessage('');
    } catch (_error) {
      setFavoriteMeals([]);
      setCatalogMeals([]);
      setErrorMessage('Nepodařilo se načíst katalog.');
    } finally {
      setTabLoading(false);
    }
  }, [debouncedSearch, dispatch, loggedUserId, routeUserId]);

  useEffect(() => {
    void fetchMyMeals();
  }, [fetchMyMeals]);

  useEffect(() => {
    if (activeTab === 1 || activeTab === 2) {
      void fetchFavoritesOrCatalog();
    }
  }, [activeTab, debouncedSearch, fetchFavoritesOrCatalog]);

  const handleTabChange = (_event: SyntheticEvent, value: number) => {
    setActiveTab(value as TabIndex);
  };

  const currentMeals = useMemo(() => {
    if (activeTab === 0) {
      return filteredMyMeals;
    }

    if (activeTab === 1) {
      return favoriteMeals;
    }

    return catalogMeals;
  }, [activeTab, catalogMeals, favoriteMeals, filteredMyMeals]);

  const handleSaveConsumed = async (meal: Meal) => {
    if (!meal.id) {
      return;
    }

    await dispatch(
      saveConsumedMeal({
        meal: {
          id: meal.id,
          languageCode: meal.languageCode
        },
        name: meal.name,
        foodstuffs: buildConsumedMealFoodstuffs(meal.foodstuffs),
        mealtime: meal.mealtime || guessMealtime(),
        consumptionTime: new Date().toISOString().slice(0, 16)
      })
    );

    setSnackbar({
      open: true,
      message: 'Jídlo bylo zaznamenáno.'
    });
  };

  const handleEdit = (meal: Meal) => {
    if (!meal.id) {
      return;
    }

    navigate(`/meal/${meal.id}`);
  };

  const handleCreateMeal = () => {
    setShowMealDialog(true);
  };

  const handleCreateFoodstuff = () => {
    setShowFoodstuffDialog(true);
  };

  const handleToggleFavorite = async (meal: Meal) => {
    if (!meal.id) {
      return;
    }

    if (!userId) {
      setSnackbar({
        open: true,
        message: 'Akci nelze dokončit bez přihlášeného uživatele.'
      });
      return;
    }

    if (meal.userMealId) {
      await dispatch(deleteUserMeal({ userMealId: meal.userMealId })).unwrap();
    } else {
      await dispatch(saveUserMealForUser({ userId, mealId: meal.id })).unwrap();
    }

    await fetchFavoritesOrCatalog();

    setSnackbar({
      open: true,
      message: meal.userMealId
        ? 'Jídlo bylo odebráno z oblíbených.'
        : 'Jídlo bylo přidáno do oblíbených.'
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar((current) => ({ ...current, open: false }));
  };

  return (
    <Box sx={{ backgroundColor: '#FBF7F2', minHeight: '100vh', p: { xs: 2, md: 4 } }}>
      <Box sx={{ maxWidth: 1180, mx: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="h4" sx={{ color: '#2E2A26', fontWeight: 700 }}>
          Jídla
        </Typography>
        <Typography variant="body1" sx={{ color: '#6F675F' }}>
          Vaše osobní knihovna jídel a veřejný katalog receptů pro rychlé vyhledání a opakované použití.
        </Typography>

        <Paper
          sx={{
            p: 2,
            borderRadius: 3,
            border: '1px solid #E6E1DB',
            backgroundColor: '#FFFFFF',
            display: 'flex',
            flexDirection: 'column',
            gap: 2
          }}
        >
          <TextField
            fullWidth
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            label="Vyhledat jídlo"
            variant="outlined"
          />

          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1.5 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateMeal}
              sx={{
                backgroundColor: '#7A4A2E',
                color: '#FBF7F2',
                '&:hover': { backgroundColor: '#5F3923' },
                textTransform: 'none',
                fontWeight: 700
              }}
              fullWidth
            >
              + Přidat jídlo
            </Button>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleCreateFoodstuff}
              sx={{
                borderColor: '#D7CFC6',
                color: '#6F675F',
                textTransform: 'none',
                fontWeight: 700
              }}
              fullWidth
            >
              + Přidat potravinu
            </Button>
          </Box>

          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label="Moje jídla" />
            <Tab label="Oblíbená" />
            <Tab label="Katalog" />
          </Tabs>
        </Paper>

        <Dialog open={showMealDialog} onClose={() => setShowMealDialog(false)} fullWidth maxWidth="md">
          <DialogTitle sx={{ color: '#2E2A26', fontWeight: 700 }}>Přidat jídlo</DialogTitle>
          <DialogContent dividers sx={{ pb: 3 }}>
            <AddMeal />
          </DialogContent>
        </Dialog>

        <Dialog
          open={showFoodstuffDialog}
          onClose={() => setShowFoodstuffDialog(false)}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle sx={{ color: '#2E2A26', fontWeight: 700 }}>Přidat potravinu</DialogTitle>
          <DialogContent dividers sx={{ pb: 3 }}>
            <AddFoodstuff />
          </DialogContent>
        </Dialog>

        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' } }}>
          {tabLoading ? (
            <Paper
              sx={{
                p: 4,
                borderRadius: 3,
                border: '1px solid #E6E1DB',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gridColumn: { xs: '1 / -1', md: undefined }
              }}
            >
              <CircularProgress size={24} sx={{ color: '#7A4A2E' }} />
            </Paper>
          ) : showEmptyState ? (
            <Paper
              sx={{
                p: 3,
                borderRadius: 3,
                border: '1px solid #E6E1DB',
                backgroundColor: '#FFFFFF',
                gridColumn: { xs: '1 / -1', md: undefined }
              }}
            >
              <Typography variant="body1" sx={{ color: '#6F675F' }}>
                {emptyText}
              </Typography>
            </Paper>
          ) : (
            currentMeals.map((meal) => (
              <MealLibraryCard
                key={meal.id}
                meal={meal}
                isFavorite={Boolean(meal.userMealId)}
                onLog={() => void handleSaveConsumed(meal)}
                onEdit={() => handleEdit(meal)}
                onToggleFavorite={() => void handleToggleFavorite(meal)}
              />
            ))
          )}

          {!tabLoading && !!errorMessage && (
            <Paper
              sx={{
                p: 2,
                borderRadius: 3,
                border: '1px solid #F2D1D1',
                backgroundColor: '#FFF0F0',
                gridColumn: { xs: '1 / -1', md: undefined }
              }}
            >
              <Alert severity="warning" onClose={() => setErrorMessage('')}>
                {errorMessage}
              </Alert>
            </Paper>
          )}

        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={2200}
        onClose={handleCloseSnackbar}
        message={snackbar.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}
